import React, { useEffect, useState, ReactNode } from 'react';
import { useParams } from 'wouter';

// Use a relative path for fetch in browser (public folder or static assets)
const errorFilePath = '/attached_assets/costco/Existing_Reports/aad_jishnu_bacpac_errors.txt';

const parseFindings = (fileContent: string, clientCode?: string) => {
  // Split by line, filter out empty lines, group by unique error, and return as array of objects with count
  const lines = fileContent
    .split(/\r?\n/)
    .map((line) => line.trim())
    // Ignore lines that are only separators (e.g., all =, -, or whitespace)
    .filter((line) => line.length > 0 && !/^([=\-\s])+$/.test(line));

  const grouped: Record<string, { count: number; examples: string[] }> = {};
  for (const line of lines) {
    // Extract the core issue to group similar problems together
    let groupKey = line;
    
    // 1. Group missing table/object references together (most comprehensive)
    const missingTableMatch = line.match(/unresolved reference to object .*?(\[[^\]]+\]\.\[[^\]]+\]\.\[[^\]]+\])/i);
    if (missingTableMatch) {
      groupKey = `Missing table/object: ${missingTableMatch[1]}`;
    }
    
    // 2. Group orphaned user issues together
    else if (/orphaned from its login/i.test(line)) {
      const userMatch = line.match(/User: \[([^\]]+)\]/i);
      if (userMatch) {
        groupKey = `Orphaned user: ${userMatch[1]}`;
      }
    }
    
    // 3. Don't group UDT issues - keep individual for specific fixes
    // 4. Don't group external reference violations - each needs specific object fix
    // 5. Don't group ambiguous references - each object needs individual attention
    // 6. Don't group SQL error codes - each error needs specific resolution
    
    // 7. Group by specific object name (more granular)
    else {
      const objectTypeMatch = line.match(/(View|Procedure|Function|Table|Index|Column): \[([^\]]+)\]/i);
      if (objectTypeMatch) {
        const objectType = objectTypeMatch[1];
        const objectName = objectTypeMatch[2];
        
        // Group by specific object name to keep tasks actionable
        groupKey = `${objectType}: [${objectName}]`;
      }
    }
    
    if (grouped[groupKey]) {
      grouped[groupKey].count++;
      if (!grouped[groupKey].examples.includes(line)) {
        grouped[groupKey].examples.push(line);
      }
    } else {
      grouped[groupKey] = { count: 1, examples: [line] };
    }
  }

  return Object.entries(grouped).map(([groupKey, { count, examples }]) => {
    const line = examples[0]; // Use first example for pattern matching
    let technical = '';
    let technicalTag = '';
    let business = '';
    let businessIcon = null;
    let businessTagColor = '';
    let suggestion = '';
    let team = '';

    // Extract object type and name first
    let objectTypeMatch = line.match(/element \[([^\]]+)\]/i);
    let objectType = objectTypeMatch ? objectTypeMatch[1] : '';
    let objectNameMatch = line.match(/View: \[([^\]]+)\]|Procedure: \[([^\]]+)\]|Function: \[([^\]]+)\]|Parameter: \[([^\]]+)\]|Table: \[([^\]]+)\]/i);
    let objectName = '';
    if (objectNameMatch) {
      objectName = objectNameMatch[1] || objectNameMatch[2] || objectNameMatch[3] || objectNameMatch[4] || objectNameMatch[5] || '';
    }
    let errorTypeMatch = line.match(/Error (SQL\d+): ([^:]+):/i);
    let errorType = errorTypeMatch ? errorTypeMatch[2] : '';

    // Deep technical assessment with Microsoft documentation links
    // Check for view-specific issues first (before generic object references)
    if (/view/i.test(line) || /View:/i.test(line) || /View issues:/i.test(groupKey)) {
      technical = `Reporting disruption. Views can be easily recreated without complex regression testing. Temporary impact on dashboards and reports. Docs: https://learn.microsoft.com/en-us/sql/relational-databases/views/views`;
      technicalTag = 'bg-red-300 text-white border border-red-400';
      business = 'Low Business Risk';
      businessIcon = (
        <svg className="inline w-5 h-5 text-red-400 mr-1" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
      );
      businessTagColor = 'bg-red-400 text-white';
    } else if (/orphaned from its login/i.test(line)) {
      technical = `Database-login mapping broken. Authentication failures will occur post-migration. Root cause: Missing server login for database user. Docs: https://learn.microsoft.com/en-us/sql/relational-databases/security/contained-database-users-making-your-database-portable`;
      technicalTag = 'bg-red-700 text-white border border-red-800';
      business = 'ACCESS DENIED: User cannot connect without login mapping';
      businessIcon = (
        <svg className="inline w-5 h-5 text-red-600 mr-1" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
      );
      businessTagColor = 'bg-red-600 text-white';
    } else if (/unresolved reference to Built-in Type/i.test(line)) {
      technical = `Invalid UDT reference. [dbo].[SYSNAME] treated as custom type but it's a system alias (NVARCHAR(128)). Causes SSDT deployment failures. Docs: https://learn.microsoft.com/en-us/sql/t-sql/data-types/nchar-and-nvarchar-transact-sql`;
      technicalTag = 'bg-red-700 text-white border border-red-800';
      business = 'TYPE CONFLICT: Custom SYSNAME clashes with SQL Server built-in';
      businessIcon = (
        <svg className="inline w-5 h-5 text-red-600 mr-1" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
      );
      businessTagColor = 'bg-red-600 text-white';
    } else if (/unresolved reference to object/i.test(line) || /Missing table\/object/i.test(groupKey)) {
      technical = `Broken dependency chain. Target object missing, misnamed, or in different database. Compilation and runtime will fail. Docs: https://learn.microsoft.com/en-us/sql/relational-databases/databases/contained-databases`;
      technicalTag = 'bg-red-700 text-white border border-red-800';
      business = 'RUNTIME FAILURE: Missing objects will cause application errors';
      businessIcon = (
        <svg className="inline w-5 h-5 text-red-600 mr-1" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
      );
      businessTagColor = 'bg-red-600 text-white';
    } else if (/external references are not supported/i.test(line)) {
      technical = 'BACPAC containment model violation. Cross-database dependencies break portability requirements. Migration will fail. Docs: https://learn.microsoft.com/en-us/sql/relational-databases/data-tier-applications/data-tier-applications';
      technicalTag = 'bg-red-700 text-white border border-red-800';
      business = 'DEPLOYMENT BLOCKER: External database references prevent BACPAC deployment';
      businessIcon = (
        <svg className="inline w-5 h-5 text-red-600 mr-1" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
      );
      businessTagColor = 'bg-red-600 text-white';
    } else if (/ambiguous because it could refer to any of the following objects/i.test(line)) {
      technical = 'Non-deterministic binding at compile time. Multiple matching objects cause unpredictable behavior across environments. Docs: https://learn.microsoft.com/en-us/sql/t-sql/statements/create-view-transact-sql#best-practices';
      technicalTag = 'bg-red-500 text-white border border-red-600';
      business = 'AMBIGUOUS REFERENCE: SQL Server cannot determine which object to use';
      businessIcon = (
        <svg className="inline w-5 h-5 text-red-600 mr-1" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
      );
      businessTagColor = 'bg-red-600 text-white';
    } else if (/index/i.test(line)) {
      technical = `Performance degradation risk. Fragmentation or suboptimal design causes increased I/O and slower query response times. Docs: https://learn.microsoft.com/en-us/sql/relational-databases/indexes/reorganize-and-rebuild-indexes`;
      technicalTag = 'bg-red-500 text-white border border-red-600';
      business = 'PERFORMANCE RISK: Slower queries may impact user experience';
      businessIcon = (
        <svg className="inline w-5 h-5 text-yellow-500 mr-1" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
      );
      businessTagColor = 'bg-orange-600 text-white';
    } else if (/procedure|function/i.test(line) || /Procedure issues:|Function issues:/i.test(groupKey)) {
      technical = `Critical business logic failure. Stored procedures and functions contain core business rules. Runtime failures will break application functionality. Docs: https://learn.microsoft.com/en-us/sql/relational-databases/databases/database-object-extended-properties`;
      technicalTag = 'bg-red-700 text-white border border-red-800';
      business = 'BUSINESS LOGIC FAILURE: Core application functions will break';
      businessIcon = (
        <svg className="inline w-5 h-5 text-red-600 mr-1" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
      );
      businessTagColor = 'bg-red-600 text-white';
    } else if (/table/i.test(line) || /Table issues:/i.test(groupKey)) {
      technical = `Data access disruption. Table schema issues can cause data corruption or application crashes. High impact on data integrity. Docs: https://learn.microsoft.com/en-us/sql/relational-databases/tables/tables`;
      technicalTag = 'bg-red-700 text-white border border-red-800';
      business = 'DATA INTEGRITY RISK: Table issues may corrupt application data';
      businessIcon = (
        <svg className="inline w-5 h-5 text-red-600 mr-1" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
      );
      businessTagColor = 'bg-red-600 text-white';
    } else if (/column/i.test(line)) {
      technical = `Schema consistency issue. Column-level problems may cause data mapping errors or application compatibility issues. Docs: https://learn.microsoft.com/en-us/sql/relational-databases/databases/database-object-extended-properties`;
      technicalTag = 'bg-red-500 text-white border border-red-600';
      business = 'SCHEMA MISMATCH: Column issues may cause data mapping errors';
      businessIcon = (
        <svg className="inline w-5 h-5 text-yellow-500 mr-1" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
      );
      businessTagColor = 'bg-orange-600 text-white';
    } else {
      technical = 'Low Risk: Minor validation warning with no functional impact.';
      technicalTag = 'bg-red-300 text-white border border-red-400';
      business = 'LOW IMPACT: Minor validation warning (safe to ignore)';
      businessIcon = (
        <svg className="inline w-5 h-5 text-red-300 mr-1" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
      );
      businessTagColor = 'bg-red-300 text-white';
    }

    // Team assignment and actionable, deadline-driven suggestions
    // Default team is Dev Team unless overridden below
    team = 'Dev Team';

    // Highly specific actionable suggestions based on error context
    // Handle view issues first (before generic object references)
    if (/view/i.test(line) || /View:/i.test(line) || /View issues:/i.test(groupKey)) {
      // Extract proper view name with schema from groupKey or objectName
      const viewName = groupKey.match(/View: \[([^\]]+)\]/)?.[1] || objectName || 'the affected view';
      suggestion = [
        `• Recreate view [${viewName}].`,
        '• Devs: Simple recreation - minimal regression testing needed.',
        '• Business: Temporary reporting disruption expected.'
      ].join('\n');
    } else if (/orphaned from its login/i.test(line)) {
      team = 'DBA Team';
      suggestion = [
        `• Re-map or drop orphaned user '${objectType || objectName}'.`,
        '• Prefer using Security Groups for user management to reduce discrepancies between source and target environments.',
        '• Use ALTER USER ... WITH LOGIN or DROP USER if not needed.'
      ].join('\n');
    } else if (/unresolved reference to Built-in Type/i.test(line)) {
      team = 'Dev Team';
      suggestion = [
        count > 1 ? `• Fix ${count} UDT reference issues in ${objectName || 'this object'}.` : `• Update procedure/parameter '${objectName}' to use a supported data type.`,
        '• Replace all [dbo].[SYSNAME] references with NVARCHAR(128).',
        '• Test all affected procedures after changes.'
      ].join('\n');
    } else if (/unresolved reference to object/i.test(line) || /Missing table\/object/i.test(groupKey)) {
      team = 'Dev Team';
      const missingObject = line.match(/object ([^\.\]]+\.[^\.\]]+\.[^\.\]]+)/i)?.[1] || groupKey.replace('Missing table/object: ', '');
      suggestion = [
        `• Create or correct missing object: ${missingObject}.`,
        count > 1 ? `• This will resolve ${count} related dependency issues.` : '• Check for typos or missing deployments.',
        '• Verify all dependent views and procedures after fix.'
      ].join('\n');
    } else if (/external references are not supported/i.test(line)) {
      team = 'Dev Team';
      suggestion = [
        count > 1 ? `• Fix ${count} cross-database reference violations in ${objectName || 'this object'}.` : '• Remove or refactor cross-database references in the view or procedure.',
        '• Consider copying required objects into the current database.',
        '• Update all affected views and procedures to use local objects.'
      ].join('\n');
    } else if (/ambiguous because it could refer to any of the following objects/i.test(line)) {
      team = 'Dev Team';
      suggestion = [
        count > 1 ? `• Fix ${count} ambiguous object references in ${objectName || 'this object'}.` : '• Disambiguate object references in the view or procedure.',
        '• Use fully qualified names (schema.object) for all references.',
        '• Review and standardize naming conventions to prevent conflicts.'
      ].join('\n');
    } else if (/index/i.test(line)) {
      team = 'Dev & DBA Team';
      suggestion = [
        `• Review and fix index usage/fragmentation for ${objectName || objectType || 'the affected object'}.`,
        '• Devs: Ensure queries use indexes efficiently.',
        '• DBAs: Rebuild or update statistics as needed.'
      ].join('\n');
    } else if (/procedure|function/i.test(line) || /Procedure:|Function:/i.test(groupKey)) {
      const objName = objectName || groupKey.match(/\[([^\]]+)\]/)?.[1] || 'the affected procedure/function';
      suggestion = [
        `• URGENT: Fix business logic in ${objName}.`,
        count > 1 ? `• This object has ${count} related issues that need attention.` : '• Devs: Review dependencies and update references.',
        '• Test all business workflows that use this object.'
      ].join('\n');
    } else if (/table/i.test(line) || /Table:/i.test(groupKey)) {
      const objName = objectName || groupKey.match(/\[([^\]]+)\]/)?.[1] || 'the affected table';
      suggestion = [
        `• CRITICAL: Resolve table schema issue for ${objName}.`,
        count > 1 ? `• This table has ${count} related issues that need attention.` : '• Devs: Check data integrity and fix references.',
        '• DBAs: Verify backup and recovery procedures.'
      ].join('\n');
    } else if (/column/i.test(line)) {
      suggestion = [
        `• Review column mapping for ${objectName || objectType || 'the affected column'}.`,
        '• Devs: Update application code to handle schema changes.'
      ].join('\n');
    } else if (/database|instance|server|permission|type/i.test(line)) {
      team = 'DBA Team';
      suggestion = [
        `• Check and resolve configuration, permissions, or orphaned objects for ${objectName || objectType || 'the affected object'}.`,
        '• DBAs: Resolve at the infrastructure or security level.'
      ].join('\n');
    } else if (errorType) {
      suggestion = [
        `• Address error type '${errorType}' in ${objectName || objectType || 'the affected object'}.`
      ].join('\n');
    } else {
      suggestion = '• Review and resolve as appropriate.';
    }

    // Special case for HighJump/holiday
    if (clientCode && clientCode.toLowerCase().includes('highjump')) {
      technical = 'Critical: Holiday/Peak Resilience Required';
      technicalTag = 'bg-red-700 text-white';
      business = 'Loss of Business';
      businessIcon = (
        <svg className="inline w-5 h-5 text-red-800 mr-1" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
      );
      businessTagColor = 'bg-red-200 text-red-900';
      team = 'Dev & DBA Team';
      suggestion = 'Ensure all critical paths are resilient and tested for peak/holiday loads.';
    }

    // Create a clear, human-readable discovery description
    let discoveryDescription = groupKey;
    
    // Make discovery descriptions much clearer and more actionable
    if (/Missing table\/object:/i.test(groupKey)) {
      const objName = groupKey.replace('Missing table/object: ', '');
      discoveryDescription = `Missing dependency: ${objName}`;
    } else if (/Orphaned user:/i.test(groupKey)) {
      const userName = groupKey.replace('Orphaned user: ', '');
      discoveryDescription = `User [${userName}] missing login mapping`;
    } else if (/View:/i.test(groupKey)) {
      const viewName = groupKey.match(/View: \[([^\]]+)\]/)?.[1];
      if (line.includes('external references are not supported')) {
        discoveryDescription = `View [${viewName}] references external database (BACPAC violation)`;
      } else if (line.includes('ambiguous because it could refer to any of the following objects')) {
        discoveryDescription = `View [${viewName}] has ambiguous object references`;
      } else if (line.includes('unresolved reference to object')) {
        discoveryDescription = `View [${viewName}] references missing objects`;
      } else {
        discoveryDescription = `View [${viewName}] has schema issues`;
      }
    } else if (/Procedure:/i.test(groupKey)) {
      const procName = groupKey.match(/Procedure: \[([^\]]+)\]/)?.[1];
      if (line.includes('unresolved reference to Built-in Type')) {
        discoveryDescription = `Procedure [${procName}] uses invalid data types (SYSNAME)`;
      } else {
        discoveryDescription = `Procedure [${procName}] has dependency issues`;
      }
    } else if (/Function:/i.test(groupKey)) {
      const funcName = groupKey.match(/Function: \[([^\]]+)\]/)?.[1];
      discoveryDescription = `Function [${funcName}] has dependency issues`;
    } else if (/Table:/i.test(groupKey)) {
      const tableName = groupKey.match(/Table: \[([^\]]+)\]/)?.[1];
      discoveryDescription = `Table [${tableName}] has schema issues`;
    } else if (/Parameter:/i.test(groupKey)) {
      const paramName = groupKey.match(/Parameter: \[([^\]]+)\]/)?.[1];
      discoveryDescription = `Parameter [${paramName}] uses invalid data type`;
    } else if (/unresolved reference to Built-in Type/i.test(line)) {
      discoveryDescription = `Invalid data type usage (SYSNAME type issue)`;
    } else if (/external references are not supported/i.test(line)) {
      discoveryDescription = `Cross-database reference (BACPAC containment violation)`;
    } else if (/ambiguous because it could refer to any of the following objects/i.test(line)) {
      discoveryDescription = `Ambiguous object reference (multiple matches)`;
    } else if (/unresolved reference to object/i.test(line)) {
      discoveryDescription = `Missing database object dependency`;
    } else if (/orphaned from its login/i.test(line)) {
      const userMatch = line.match(/element \[([^\]]+)\]/i);
      const userName = userMatch ? userMatch[1] : 'Unknown User';
      discoveryDescription = `User [${userName}] orphaned from login`;
    }
    
    // Add count suffix if multiple instances
    if (count > 1) {
      discoveryDescription += ` (${count} instances)`;
    }

    return {
      discovery: discoveryDescription,
      technical,
      technicalTag,
      business,
      businessIcon,
      businessTagColor,
      suggestion,
      team,
      count
    };
  });
};

const BacpacAssessmentReport: React.FC = () => {
  const { clientCode } = useParams<{ clientCode: string }>();
  const [findings, setFindings] = useState<{
    discovery: string;
    technical: string;
    technicalTag: string;
    business: string;
    businessIcon: ReactNode;
    businessTagColor: string;
    suggestion: string;
    team: string;
    count: number;
  }[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastPublished, setLastPublished] = useState<string | null>(null);

  useEffect(() => {
    // Try to fetch the file and get its last-modified header
    fetch(errorFilePath)
      .then((res) => {
        if (!res.ok) throw new Error('Failed to load error file');
        const lastMod = res.headers.get('last-modified');
        if (lastMod) {
          setLastPublished(new Date(lastMod).toLocaleString());
        } else {
          // fallback: static date or today's date
          setLastPublished('September 13, 2025');
        }
        return res.text();
      })
      .then((text) => {
        setFindings(parseFindings(text, clientCode));
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  }, [clientCode]);

  // CSV export handler
  const handleDownloadCSV = () => {
    const headers = ['#', 'Discoveries', 'Technical Assessments', 'Business Impacts', 'Suggestions', 'Teams'];
    const rows = findings.map((f, i) => [
      i + 1,
      f.discovery.replace(/\n/g, ' '),
      f.technical,
      f.business,
      f.suggestion.replace(/\n/g, ' '),
      f.team
    ]);
    const csvContent = [headers, ...rows]
      .map(row => row.map(field => '"' + String(field).replace(/"/g, '""') + '"').join(','))
      .join('\r\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `bacpac-assessment-report${clientCode ? '-' + clientCode : ''}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  let content;
  if (loading) {
    content = <div className="text-gray-500">Loading findings...</div>;
  } else if (error) {
    content = <div className="text-red-600">Error: {error}</div>;
  } else if (findings.length === 0) {
    content = <div className="text-gray-400 italic">This report is currently empty.</div>;
  } else {
    content = <>
      <button
        className="mb-2 px-2 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700 transition"
        onClick={handleDownloadCSV}
      >
        Export CSV
      </button>
      <table className="w-full border border-gray-300 text-xs">
        <thead>
          <tr className="bg-gray-100">
            <th className="border px-1 py-1 text-left w-8">#</th>
            <th className="border px-1 py-1 text-left w-72">Discovery</th>
            <th className="border px-1 py-1 text-left w-32">Technical</th>
            <th className="border px-1 py-1 text-left w-20">Business</th>
            <th className="border px-1 py-1 text-left w-48">Action</th>
            <th className="border px-1 py-1 text-left w-24">Team</th>
          </tr>
        </thead>
        <tbody>
          {findings.map((f, i) => (
            <tr key={f.discovery + i} className="align-top">
              <td className="border px-1 py-1 font-bold text-center text-xs">{i + 1}</td>
              <td className="border px-1 py-1 text-xs">
                {(() => {
                  // Compact discovery text - remove redundant "Error" prefixes and show only essential info
                  let displayText = f.discovery;
                  if (displayText.includes('(') && displayText.includes('instances)')) {
                    // Show count in a more compact way
                    displayText = displayText.replace(' instances)', ')');
                  }
                  // Highlight key parts in red but keep compact
                  const errorMatch = displayText.match(/(Error [^:]+: [^:]+:)(.*)/i);
                  if (errorMatch) {
                    return <><span className="text-red-600 font-medium text-xs">{errorMatch[1]}</span><span className="text-xs">{errorMatch[2]}</span></>;
                  }
                  return <span className="text-xs">{displayText}</span>;
                })()}
              </td>
              <td className="border px-1 py-1">
                <div className={`rounded px-1 py-0.5 text-xs font-medium ${f.technicalTag}`}>
                  {/* Show useful technical details instead of redundant severity labels */}
                  {f.technical.includes('UDT') ? 'UDT Type Error' :
                   f.technical.includes('Broken dependency') ? 'Missing Object' :
                   f.technical.includes('BACPAC') ? 'Cross-DB Ref' :
                   f.technical.includes('Authentication') ? 'Login Missing' :
                   f.technical.includes('Non-deterministic') ? 'Ambiguous Ref' :
                   f.technical.includes('Performance') ? 'Index Issue' :
                   f.technical.includes('business logic') ? 'Proc/Func Error' :
                   f.technical.includes('Data access') ? 'Table Schema' :
                   f.technical.includes('Schema consistency') ? 'Column Issue' :
                   f.technical.includes('Reporting') ? 'Issue with View' :
                   f.technical.includes('Low Risk') ? 'Minor Warning' : 'Validation'}
                </div>
              </td>
              <td className="border px-1 py-1">
                <div className={`inline-flex items-center rounded px-1 py-0.5 text-xs ${f.businessTagColor}`}>
                  {f.businessIcon}
                  {/* Compact business impact */}
                  {f.business.includes('Low') ? 'Low' :
                   f.business.includes('Severe') ? 'Severe' :
                   f.business.includes('Potential') ? 'Moderate' :
                   f.business.includes('Minimal') ? 'Minimal' : f.business}
                </div>
              </td>
              <td className="border px-1 py-1 text-xs">
                {/* Compact suggestions - show only the main action */}
                {(() => {
                  const firstLine = f.suggestion.split('\n')[0];
                  return firstLine.replace(/^•\s*/, '');
                })()}
              </td>
              <td className="border px-1 py-1">
                <div className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                  f.team === 'Dev Team' ? 'bg-shiftleft-100 text-shiftleft-800' :
                  f.team === 'DBA Team' ? 'bg-green-100 text-green-800' :
                  f.team === 'Dev & DBA Team' ? 'bg-purple-100 text-purple-800' :
                  'bg-gray-100 text-gray-800'
                }`}>
                  {f.team}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </>;
  }

  return (
    <div className="max-w-7xl mx-auto p-4 bg-white">
      <h1 className="text-xl font-bold mb-1">Bacpac Assessment Report {clientCode ? `- ${clientCode}` : ''}</h1>
      {lastPublished && (
        <div className="text-xs text-gray-500 mb-2">Published: {lastPublished}</div>
      )}
      {content}
    </div>
  );
};

export default BacpacAssessmentReport;
